export type DesignQualityStatus = 'pass' | 'warn' | 'fail' | 'metric-only'
export type DesignQualityEvidenceSourceKind = 'measured' | 'asserted' | 'fixture' | 'metric-only'

export interface DesignQualityEvidenceModel {
  status: DesignQualityStatus
  evidence: {
    sourceKind: DesignQualityEvidenceSourceKind
    measured: boolean
    measuredBy?: string
    capturedAt?: string
    screenshotArtifactCount: number
    interactionTraceArtifactCount: number
  }
  summary: {
    score: number
    grade: string
    improvementDelta: number
    hardeningPassed: boolean
    artifactCount: number
    unsupportedCapabilityCount: number
    rawStyleLeakCount: number
    authorStyleObjectCount: number
    unconvertedStyleObjectCount: number
    accessibilityGapCount: number
  }
  issues: {
    evidenceSource: DesignQualityIssue[]
    styleAuthority: DesignQualityIssue[]
    rawStyleLeaks: DesignQualityIssue[]
    unsupportedCapabilities: DesignQualityIssue[]
    visualDiffs: DesignQualityIssue[]
    accessibilityGaps: DesignQualityIssue[]
  }
  artifacts: DesignQualityArtifactGroup[]
  referoStitchFacts: ReferoStitchFactEvidence[]
  generatorFeedback: DesignQualityGeneratorFeedbackModel
  profileSuite: DesignQualityProfileSuiteModel | null
}

export interface DesignQualityIssue {
  id: string
  kind: 'evidence-source' | 'style-authority' | 'raw-style-leak' | 'unsupported-capability' | 'visual-diff' | 'accessibility-gap'
  severity: 'error' | 'warning' | 'info'
  message: string
  detail?: string
  sourceId?: string
  normalizedTarget?: string
}

export interface DesignQualityArtifactGroup {
  viewport: string
  status: 'available' | 'metric-only'
  currentPng?: string
  referencePng?: string
  diffPng?: string
  sourceId?: string
}

export interface ReferoStitchFactEvidence {
  id: string
  kind: string
  reviewStatus: 'accepted' | 'pending-review' | 'quarantine'
  sourceSystem: string
  sourceId: string
  confidence: number
  normalizedTarget: string
  rawValue?: string
  quarantineExcludedFromGenerator: boolean
}

export interface DesignQualityGeneratorFeedbackModel {
  shouldRetry: boolean
  fallbackAllowedHardConstraintsPreserved: boolean
  negativeConstraints: string[]
  fallbackInstructions: string[]
  blockedReasons: string[]
}

export interface DesignQualityProfileSuiteModel {
  summary: {
    total: number
    averageScore: number
    averageImprovement: number
    gradeCounts: Record<string, number>
    artifactCount: number
    unsupportedCapabilityCount: number
    hardeningPassed: boolean
  }
  rows: DesignQualityProfileRow[]
}

export interface DesignQualityProfileRow {
  id: string
  label: string
  score: number
  grade: string
  rawLeaks: number
  styleObjects: number
  visualFidelity: number
  capabilityEvidence: number
}

const REPORT_KEYS = [
  'uiDesignQualityBenchmark',
  'uiDesignQualityBenchmarkReport',
  'uiDesignQuality',
  'designQualityBenchmark',
  'designQuality',
] as const

const SUITE_KEYS = [
  'uiDesignQualityBenchmarkSuite',
  'uiDesignQualityProfileSuite',
  'designQualitySuite',
  'profileSuite',
] as const

const INGESTION_KEYS = [
  'referoStitchIngestion',
  'referoStitchEvidence',
  'referoStitch',
] as const

const FEEDBACK_KEYS = [
  'uiDesignQualityGeneratorFeedback',
  'designQualityFeedback',
  'generatorFeedback',
] as const

export function readDesignQualityEvidenceModel(source: unknown): DesignQualityEvidenceModel | null {
  const candidates = collectCandidateRecords(source)
  const report = readFirstRecordFromCandidates(candidates, REPORT_KEYS)
  const suite = readFirstRecordFromCandidates(candidates, SUITE_KEYS)
  const ingestion = readFirstRecordFromCandidates(candidates, INGESTION_KEYS)
  const feedback = readFirstRecordFromCandidates(candidates, FEEDBACK_KEYS)

  const suiteModel = buildProfileSuiteModel(suite)
  const reportFromSuite = readRecord(readArray(suite?.profiles)?.[0])?.report
  const selectedReport = report ?? readRecord(reportFromSuite)

  if (!selectedReport && !suiteModel && !ingestion && !feedback) return null

  const fallbackReport = selectedReport ?? {}
  const artifacts = readArtifacts(fallbackReport)
  const evidenceSource = readEvidenceSource(fallbackReport, artifacts)
  const unsupportedCapabilities = readRendererCapabilities(fallbackReport)
    .filter((capability) => capability.support === 'unsupported')
  const rawStyleLeakCount = readNumberPath(fallbackReport, ['measurements', 'leakedRawStyleValues'])
  const authorStyleObjectCount = readNumberPath(fallbackReport, ['measurements', 'authorStyleObjectCount'])
  const unconvertedStyleObjectCount = readNumberPath(fallbackReport, ['measurements', 'unconvertedStyleObjectCount'])
  const accessibilityScore = readNumberPath(fallbackReport, ['dimensions', 'accessibilityCoverage', 'score'], 100)
  const visualFidelityScore = readNumberPath(fallbackReport, ['dimensions', 'visualFidelity', 'score'], 100)
  const score = readNumber(fallbackReport.score, suiteModel?.summary.averageScore ?? 0)
  const grade = readString(fallbackReport.grade, gradeFromScore(score))
  // Fail-closed: an asserted hardeningPassed (from the report or suite
  // summary — both ride in the artifact under validation) may only make the
  // verdict stricter. Without the AND, a report carrying
  // `hardeningPassed: true` skipped the score/leak/style-authority checks
  // entirely and rendered `pass` while its own issue list held errors.
  const measuredHardeningPassed =
    score >= 85 &&
    rawStyleLeakCount === 0 &&
    authorStyleObjectCount === 0 &&
    unconvertedStyleObjectCount === 0 &&
    unsupportedCapabilities.length === 0
  const assertedHardeningPassed = readBoolean(
    fallbackReport.hardeningPassed,
    suiteModel?.summary.hardeningPassed ?? measuredHardeningPassed,
  )
  const hardeningPassed = assertedHardeningPassed && measuredHardeningPassed

  const rawStyleLeaks = rawStyleLeakCount > 0
    ? issueFromDetails(
        'raw-style-leak',
        'error',
        readStringArrayPath(fallbackReport, ['dimensions', 'rawStyleSafety', 'details']),
        `Detected ${rawStyleLeakCount} raw style leak(s).`,
      )
    : []
  const styleAuthority = authorStyleObjectCount > 0 || unconvertedStyleObjectCount > 0
    ? issueFromDetails(
        'style-authority',
        unconvertedStyleObjectCount > 0 ? 'error' : 'warning',
        readStringArrayPath(fallbackReport, ['dimensions', 'rawStyleSafety', 'details']),
        `Detected ${authorStyleObjectCount} author style object(s), ${unconvertedStyleObjectCount} unconverted style object(s).`,
      )
    : []
  const visualDiffs = visualFidelityScore < 100
    ? issueFromDetails(
        'visual-diff',
        visualFidelityScore < 85 ? 'warning' : 'info',
        readStringArrayPath(fallbackReport, ['dimensions', 'visualFidelity', 'details']),
        artifacts.length > 0
          ? 'Screenshot visual diff evidence is attached.'
          : 'Contract metric only: screenshot artifacts were not attached.',
      )
    : []
  const accessibilityGaps = accessibilityScore < 100
    ? issueFromDetails(
        'accessibility-gap',
        'warning',
        readStringArrayPath(fallbackReport, ['dimensions', 'accessibilityCoverage', 'details']),
        'Interactive controls have accessibility coverage gaps.',
      )
    : []
  const evidenceSourceIssues = buildEvidenceSourceIssues(evidenceSource, artifacts)

  const facts = readReferoStitchFacts(ingestion, source)
  const generatorFeedback = buildGeneratorFeedback(
    feedback,
    fallbackReport,
    rawStyleLeakCount,
    authorStyleObjectCount,
    unconvertedStyleObjectCount,
    unsupportedCapabilities.length,
    visualFidelityScore,
    accessibilityScore,
  )
  appendEvidenceSourceBlockedReason(generatorFeedback, evidenceSource, artifacts)

  return {
    status: artifacts.length === 0
      ? 'metric-only'
      : evidenceSource.sourceKind !== 'measured'
        ? 'fail'
        : hardeningPassed
          ? 'pass'
          : score < 85 || rawStyleLeakCount > 0 || unconvertedStyleObjectCount > 0
            ? 'fail'
            : 'warn',
    evidence: {
      sourceKind: evidenceSource.sourceKind,
      measured: evidenceSource.sourceKind === 'measured',
      ...(evidenceSource.measuredBy ? { measuredBy: evidenceSource.measuredBy } : {}),
      ...(evidenceSource.capturedAt ? { capturedAt: evidenceSource.capturedAt } : {}),
      screenshotArtifactCount: artifacts.filter((artifact) => artifact.status === 'available').length,
      interactionTraceArtifactCount: evidenceSource.interactionTraceArtifactCount,
    },
    summary: {
      score,
      grade,
      improvementDelta: readNumberPath(fallbackReport, ['improvement', 'delta']),
      hardeningPassed,
      artifactCount: artifacts.length,
      unsupportedCapabilityCount: unsupportedCapabilities.length,
      rawStyleLeakCount,
      authorStyleObjectCount,
      unconvertedStyleObjectCount,
      accessibilityGapCount: accessibilityGaps.length,
    },
    issues: {
      evidenceSource: evidenceSourceIssues,
      styleAuthority,
      rawStyleLeaks,
      unsupportedCapabilities: unsupportedCapabilities.map((capability, index) => ({
        id: `unsupported-capability:${index}`,
        kind: 'unsupported-capability',
        severity: 'warning',
        message: `${capability.componentType}:${capability.dimension} is unsupported in ${capability.packageName}.`,
        detail: capability.evidence,
        normalizedTarget: `${capability.componentType}.${capability.dimension}`,
      })),
      visualDiffs,
      accessibilityGaps,
    },
    artifacts: artifacts.length > 0
      ? artifacts
      : [{ viewport: 'contract', status: 'metric-only' }],
    referoStitchFacts: facts,
    generatorFeedback,
    profileSuite: suiteModel,
  }
}

export function designQualityIssuesForConformance(
  model: DesignQualityEvidenceModel | null,
): DesignQualityIssue[] {
  if (!model) return []
  return [
    ...model.issues.evidenceSource,
    ...model.issues.styleAuthority,
    ...model.issues.rawStyleLeaks,
    ...model.issues.visualDiffs,
    ...model.issues.accessibilityGaps,
    ...model.issues.unsupportedCapabilities,
  ]
}

function collectCandidateRecords(source: unknown): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = []
  const seen = new Set<unknown>()
  const visit = (value: unknown, depth: number) => {
    if (depth > 4 || seen.has(value)) return
    const record = readRecord(value)
    if (!record) return
    seen.add(value)
    records.push(record)
    for (const key of ['metadata', 'manifest', 'evidence', 'validation', 'quality']) {
      visit(record[key], depth + 1)
    }
    const blockRecord = readRecord(record.blocks)
    if (blockRecord) {
      for (const block of Object.values(blockRecord)) visit(block, depth + 1)
    }
    const blockArray = readArray(record.blocks)
    if (blockArray) {
      for (const block of blockArray) visit(block, depth + 1)
    }
  }
  visit(source, 0)
  return records
}

function readFirstRecordFromCandidates<T extends readonly string[]>(
  candidates: Record<string, unknown>[],
  keys: T,
): Record<string, unknown> | null {
  for (const candidate of candidates) {
    for (const key of keys) {
      const record = readRecord(candidate[key])
      if (record) return record
    }
  }
  return null
}

function readArtifacts(report: Record<string, unknown>): DesignQualityArtifactGroup[] {
  const artifacts = readArray(readRecord(report.evidence)?.screenshotArtifacts)
  if (!artifacts) return []
  return artifacts.flatMap((item, index) => {
    const record = readRecord(item)
    if (!record) return []
    const viewport = readString(record.viewport, `viewport-${index + 1}`)
    const currentPng = readString(record.currentPng)
    const referencePng = readString(record.referencePng)
    const diffPng = readString(record.diffPng)
    return [{
      viewport,
      status: currentPng || referencePng || diffPng ? 'available' as const : 'metric-only' as const,
      ...(currentPng ? { currentPng } : {}),
      ...(referencePng ? { referencePng } : {}),
      ...(diffPng ? { diffPng } : {}),
      ...(readString(record.sourceId) ? { sourceId: readString(record.sourceId) } : {}),
    }]
  })
}

function readEvidenceSource(
  report: Record<string, unknown>,
  artifacts: DesignQualityArtifactGroup[],
): {
  sourceKind: DesignQualityEvidenceSourceKind
  measuredBy?: string
  capturedAt?: string
  interactionTraceArtifactCount: number
} {
  const evidence = readRecord(report.evidence)
  const provenance = readRecord(report.provenance)
  const sourceKind =
    normalizeEvidenceSourceKind(report.sourceKind) ??
    normalizeEvidenceSourceKind(evidence?.sourceKind) ??
    normalizeEvidenceSourceKind(provenance?.sourceKind) ??
    (artifacts.length === 0 ? 'metric-only' : 'asserted')
  const measuredBy =
    readString(report.measuredBy) ||
    readString(evidence?.measuredBy) ||
    readString(provenance?.measuredBy)
  const capturedAt =
    readString(report.capturedAt) ||
    readString(evidence?.capturedAt) ||
    readString(provenance?.capturedAt)
  const interactionTraceArtifactCount =
    readArray(evidence?.interactionTraceArtifacts)?.length ??
    readArray(evidence?.actionFlowTraceArtifacts)?.length ??
    readNumberPath(report, ['measurements', 'interactionTraceArtifactCount'])

  return {
    sourceKind,
    ...(measuredBy ? { measuredBy } : {}),
    ...(capturedAt ? { capturedAt } : {}),
    interactionTraceArtifactCount,
  }
}

function buildEvidenceSourceIssues(
  evidenceSource: {
    sourceKind: DesignQualityEvidenceSourceKind
    measuredBy?: string
    capturedAt?: string
  },
  artifacts: DesignQualityArtifactGroup[],
): DesignQualityIssue[] {
  if (artifacts.length === 0) {
    return [{
      id: 'evidence-source:metric-only',
      kind: 'evidence-source',
      severity: 'error',
      message: 'Measured screenshot evidence is missing; metric-only quality cannot be promoted to ontology.',
      detail: 'Attach rendered browser/renderer artifacts before opening Refero ontology batches.',
    }]
  }
  if (evidenceSource.sourceKind === 'measured') return []
  return [{
    id: `evidence-source:${evidenceSource.sourceKind}`,
    kind: 'evidence-source',
    severity: 'error',
    message: `Design quality evidence source is ${evidenceSource.sourceKind}; measured evidence is required.`,
    detail: 'A screenshot artifact path alone is not proof that the generated application was rendered and measured.',
  }]
}

function appendEvidenceSourceBlockedReason(
  feedback: DesignQualityGeneratorFeedbackModel,
  evidenceSource: { sourceKind: DesignQualityEvidenceSourceKind },
  artifacts: DesignQualityArtifactGroup[],
): void {
  const reasons = [
    ...(artifacts.length === 0 ? ['design-quality-evidence:metric-only'] : []),
    ...(evidenceSource.sourceKind !== 'measured'
      ? [`design-quality-evidence-source:${evidenceSource.sourceKind}`]
      : []),
  ]
  for (const reason of reasons) {
    if (!feedback.blockedReasons.includes(reason)) feedback.blockedReasons.push(reason)
  }
  if (reasons.length > 0) feedback.shouldRetry = true
}

function readRendererCapabilities(report: Record<string, unknown>): Array<{
  componentType: string
  dimension: string
  packageName: string
  support: string
  evidence: string
}> {
  const capabilities = readArray(readRecord(report.evidence)?.rendererCapabilities)
  if (!capabilities) return []
  return capabilities.flatMap((item) => {
    const record = readRecord(item)
    if (!record) return []
    return [{
      componentType: readString(record.componentType, 'component'),
      dimension: readString(record.dimension, 'capability'),
      packageName: readString(record.packageName, 'renderer'),
      support: readString(record.support, 'unknown'),
      evidence: readString(record.evidence, 'No renderer evidence supplied.'),
    }]
  })
}

function readReferoStitchFacts(
  ingestion: Record<string, unknown> | null,
  source: unknown,
): ReferoStitchFactEvidence[] {
  const facts = readArray(ingestion?.facts) ?? []
  const generationContext = readRecord(ingestion?.generationContext)
    ?? readFirstRecordFromCandidates(collectCandidateRecords(source), ['generationContext'])
  const acceptedContext = JSON.stringify(generationContext?.acceptedFacts ?? [])
  return facts.flatMap((item, index) => {
    const fact = readRecord(item)
    if (!fact) return []
    const reviewStatus = normalizeReviewStatus(fact.reviewStatus)
    const normalizedTarget = readString(fact.normalizedTarget, 'unmapped')
    return [{
      id: `refero-stitch:${index}`,
      kind: readString(fact.kind, 'Fact'),
      reviewStatus,
      sourceSystem: readString(fact.sourceSystem, 'unknown'),
      sourceId: readString(fact.sourceId, 'unknown'),
      confidence: readNumber(fact.confidence),
      normalizedTarget,
      ...(readString(fact.rawValue) ? { rawValue: readString(fact.rawValue) } : {}),
      quarantineExcludedFromGenerator:
        reviewStatus !== 'quarantine' || !acceptedContext.includes(normalizedTarget),
    }]
  })
}

function buildGeneratorFeedback(
  feedback: Record<string, unknown> | null,
  report: Record<string, unknown>,
  rawStyleLeaks: number,
  authorStyleObjects: number,
  unconvertedStyleObjects: number,
  unsupportedCapabilities: number,
  visualFidelityScore: number,
  accessibilityScore: number,
): DesignQualityGeneratorFeedbackModel {
  const score = readNumber(report.score)
  const negativeConstraints = readStringArray(feedback?.negativeConstraints)
  const fallbackInstructions = readStringArray(feedback?.fallbackInstructions)
  const blockedReasons = readStringArray(feedback?.blockedReasons)

  if (negativeConstraints.length === 0) {
    if (rawStyleLeaks > 0) {
      negativeConstraints.push('Do not emit raw hex/rgb/arbitrary Tailwind/glass style values; normalize them to accepted Coogo token refs or @ds handles.')
    }
    if (authorStyleObjects > 0 || unconvertedStyleObjects > 0) {
      negativeConstraints.push('Do not emit author-authored style objects in generated Coogo UI; normalize design through Coogo-owned utility handles and typed layout facts.')
    }
    if (visualFidelityScore < 85) {
      negativeConstraints.push('Reduce screenshot visual diff by preserving normalized layout, spacing, typography, and tokenized color intent.')
    }
    if (accessibilityScore < 100) {
      negativeConstraints.push('All interactive controls must carry visible text, label, placeholder, alt, or aria label.')
    }
    if (unsupportedCapabilities > 0) {
      negativeConstraints.push('Do not use renderer-unsupported component capabilities unless a supported RendererCapabilityFact exists.')
    }
  }

  if (fallbackInstructions.length === 0) {
    fallbackInstructions.push(
      'Keep benchmark-derived negative constraints as hard rules during LLM fallback.',
      'If design quality cannot be restored from known ontology facts, return candidate facts for review.',
    )
  }

  if (blockedReasons.length === 0 && score > 0 && score < 85) {
    blockedReasons.push(`ui-design-quality-score:${score}`)
  }

  return {
    shouldRetry: readBoolean(feedback?.shouldRetry, negativeConstraints.length > 0 || blockedReasons.length > 0),
    fallbackAllowedHardConstraintsPreserved:
      negativeConstraints.length > 0 &&
      fallbackInstructions.some((item) => /hard rules|hard constraints/i.test(item)),
    negativeConstraints,
    fallbackInstructions,
    blockedReasons,
  }
}

function buildProfileSuiteModel(suite: Record<string, unknown> | null): DesignQualityProfileSuiteModel | null {
  if (!suite) return null
  const summary = readRecord(suite.summary)
  const profiles = readArray(suite.profiles) ?? []
  const rows = profiles.flatMap((item, index) => {
    const record = readRecord(item)
    const report = readRecord(record?.report)
    if (!record || !report) return []
    return [{
      id: readString(record.id, `profile-${index + 1}`),
      label: readString(record.label, `Profile ${index + 1}`),
      score: readNumber(report.score),
      grade: readString(report.grade, gradeFromScore(readNumber(report.score))),
      rawLeaks: readNumberPath(report, ['measurements', 'leakedRawStyleValues']),
      styleObjects: readNumberPath(report, ['measurements', 'authorStyleObjectCount']),
      visualFidelity: readNumberPath(report, ['dimensions', 'visualFidelity', 'score']),
      capabilityEvidence: readNumberPath(report, ['measurements', 'unsupportedCapabilityCount']),
    }]
  })

  return {
    summary: {
      total: readNumber(summary?.total, rows.length),
      averageScore: readNumber(summary?.averageScore),
      averageImprovement: readNumber(summary?.averageImprovement),
      gradeCounts: (readRecord(summary?.gradeCounts) as Record<string, number> | null) ?? {},
      artifactCount: readNumber(summary?.artifactCount),
      unsupportedCapabilityCount: readNumber(summary?.unsupportedCapabilityCount),
      hardeningPassed: readBoolean(summary?.hardeningPassed),
    },
    rows,
  }
}

function issueFromDetails(
  kind: DesignQualityIssue['kind'],
  severity: DesignQualityIssue['severity'],
  details: string[],
  fallback: string,
): DesignQualityIssue[] {
  const messages = details.length > 0 ? details : [fallback]
  return messages.map((message, index) => ({
    id: `${kind}:${index}`,
    kind,
    severity,
    message,
  }))
}

function normalizeReviewStatus(value: unknown): ReferoStitchFactEvidence['reviewStatus'] {
  if (value === 'accepted' || value === 'pending-review' || value === 'quarantine') return value
  return 'pending-review'
}

function normalizeEvidenceSourceKind(value: unknown): DesignQualityEvidenceSourceKind | null {
  if (value === 'measured' || value === 'asserted' || value === 'fixture' || value === 'metric-only') {
    return value
  }
  if (value === 'synthetic' || value === 'mock' || value === 'example') return 'fixture'
  if (value === 'manual' || value === 'reported') return 'asserted'
  return null
}

function readNumberPath(record: Record<string, unknown>, path: string[], fallback = 0): number {
  let current: unknown = record
  for (const key of path) {
    current = readRecord(current)?.[key]
  }
  return readNumber(current, fallback)
}

function readStringArrayPath(record: Record<string, unknown>, path: string[]): string[] {
  let current: unknown = record
  for (const key of path) {
    current = readRecord(current)?.[key]
  }
  return readStringArray(current)
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function readArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((item) => typeof item === 'string' ? [item] : [])
    : []
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function gradeFromScore(score: number): string {
  if (score >= 92) return 'A'
  if (score >= 85) return 'B'
  if (score >= 70) return 'C'
  return 'D'
}
